import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, lang });
      if (res.data?.status === "success") {
        toast.success(t.contact.success);
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error(t.contact.error);
      }
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F2EEE3]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-3xl mx-auto mb-14 md:mb-20"
        >
          <div className="font-eyebrow text-2xl md:text-[30px] text-[#0F3D2E]/85 mb-6">{t.contact.eyebrow}</div>
          <h2 className="font-serif-editorial text-[#0F3D2E] leading-[0.95] text-[10vw] sm:text-[8vw] md:text-[4.8vw]">
            {t.contact.title.split("\n").map((l, i) => (
              <span key={i} className="block">
                {i === 1 ? <em className="italic text-[#2E8B4A]">{l}</em> : l}
              </span>
            ))}
          </h2>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="max-w-3xl mx-auto bg-[#FAF7F0] rounded-2xl p-8 md:p-12 border border-[#E5DFCE] shadow-[0_30px_80px_-30px_rgba(15,61,46,0.25)]"
          onSubmit={onSubmit}
          data-testid="contact-form"
        >
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-[13px] font-semibold tracking-wide uppercase text-[#0F3D2E] mb-2">
                  {t.contact.name}
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  type="text"
                  data-testid="contact-input-name"
                  className="w-full bg-white border-2 border-[#E5DFCE] rounded-lg px-4 py-3 text-[16px] focus:outline-none focus:border-[#0F3D2E] text-[#0A1F17] placeholder-[#0F3D2E]/40 transition-colors"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-[13px] font-semibold tracking-wide uppercase text-[#0F3D2E] mb-2">
                  {t.contact.email}
                </label>
                <input
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  type="email"
                  data-testid="contact-input-email"
                  className="w-full bg-white border-2 border-[#E5DFCE] rounded-lg px-4 py-3 text-[16px] focus:outline-none focus:border-[#0F3D2E] text-[#0A1F17] transition-colors"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[13px] font-semibold tracking-wide uppercase text-[#0F3D2E] mb-2">
                  {t.contact.phone}
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  type="tel"
                  data-testid="contact-input-phone"
                  className="w-full bg-white border-2 border-[#E5DFCE] rounded-lg px-4 py-3 text-[16px] focus:outline-none focus:border-[#0F3D2E] text-[#0A1F17] transition-colors"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[13px] font-semibold tracking-wide uppercase text-[#0F3D2E] mb-2">
                  {t.contact.message}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  data-testid="contact-input-message"
                  className="w-full bg-white border-2 border-[#E5DFCE] rounded-lg px-4 py-3 text-[16px] focus:outline-none focus:border-[#0F3D2E] text-[#0A1F17] resize-none transition-colors"
                />
              </div>
            </div>
            <div className="mt-8 flex items-center justify-between gap-4">
              <p className="text-[12px] tracking-[0.14em] uppercase text-[#0A1F17]/50">
                * pakla.net
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 h-14 px-8 rounded-full bg-[#0F3D2E] text-[#FAF7F0] text-[14px] font-semibold tracking-[0.2em] uppercase hover:bg-[#2E8B4A] transition-colors disabled:opacity-60 shadow-lg"
              >
                {loading ? t.contact.sending : t.contact.submit}
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6h8m0 0L7 3m3 3L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </motion.form>
      </div>
    </section>
  );
};

export default Contact;
